import React, { useState } from 'react';
import { Form, Button, Alert } from 'react-bootstrap';
import { useAuth } from '../../api/auth/store';
import Loading from '../../components/Loading';
import { requestDeleteAccount } from '../../api/auth/actions';
import useUXStore from '../../api/ux/store';
import { redirectToLogin } from '../../utils/functions';

interface DeleteAccountProps {
    dialogId: string;
}

const DeleteAccount: React.FC<DeleteAccountProps> = ({ dialogId }) => {
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [confirmText, setConfirmText] = useState('');

    const { hideDialog, addToast } = useUXStore(state => state);

    const handleDelete = async (e: React.FormEvent) => {
        e.preventDefault();

        if (confirmText !== 'DELETE') {
            setError('Please type DELETE to confirm');
            return;
        }

        setLoading(true);
        setError('');

        try {
            await requestDeleteAccount(password);
            addToast('Account Deleted', 'Your account has been permanently deleted.', null, 'success');
            hideDialog(dialogId);
            // Wait a bit for the toast to be seen before redirecting
            setTimeout(() => {
                redirectToLogin();
            }, 2000);
        } catch (err: any) {
            setError(err.message || 'Failed to delete account. Please check your password.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Form onSubmit={handleDelete}>
            <Alert variant="danger">
                <Alert.Heading>Warning: Permanent Action</Alert.Heading>
                <p>
                    Deleting your account is permanent and cannot be undone. All your data,
                    including associated resources and configurations, will be lost forever.
                </p>
            </Alert>

            <Form.Group className="mb-3">
                <Form.Label>To confirm deletion, please type <strong>DELETE</strong></Form.Label>
                <Form.Control
                    type="text"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="Type DELETE"
                    required
                />
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>Confirm with your password</Form.Label>
                <div className="input-group">
                    <Form.Control
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your password"
                        required
                        autoComplete="current-password"
                    />
                    <Button
                        variant="outline-secondary"
                        onClick={() => setShowPassword(!showPassword)}
                        style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                    >
                        {showPassword ? (
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288l.338.338zM8.857 7.202l.642.642a2.313 2.313 0 0 1-.363.88l.425.425c.406-.508.603-1.155.539-1.815-.064-.66-.35-1.28-.81-1.74a2.296 2.296 0 0 0-1.74-.81c-.66.064-1.307.261-1.815.6)" />
                                <path d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829l.822.822zm-2.943 1.299l.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z" />
                                <path d="M3.35 5.47c-.18.16-.353.322-.518.487A13.134 13.134 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7.029 7.029 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.709zm10.296 8.884l-12-12 .708-.708 12 12-.708.708z" />
                            </svg>
                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                <path d="M10.5 8a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0z" />
                                <path d="M0 8s3-5.5 8-5.5S16 8 16 8s-3 5.5-8 5.5S0 8 0 8zm8 3.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z" />
                            </svg>
                        )}
                    </Button>
                </div>
            </Form.Group>

            {error && <Alert variant="danger">{error}</Alert>}

            <div className="d-flex justify-content-end gap-2 mt-4">
                <Button
                    variant="secondary"
                    onClick={() => hideDialog(dialogId)}
                    disabled={loading}
                >
                    Cancel
                </Button>
                <Button
                    type="submit"
                    variant="danger"
                    disabled={loading || confirmText !== 'DELETE'}
                >
                    {loading ? <Loading /> : 'Permanently Delete'}
                </Button>
            </div>
        </Form>
    );
};

export default DeleteAccount;
